import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { rootUrl } from "../../../config/config";

const Page = ({ children }) => (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
        <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-xl p-6">{children}</div>
    </div>
);

export default function TransactionPosting() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState("purchase");
    const [vendors, setVendors] = useState([]);
    const [parts, setParts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    // Purchase form state
    const [purchaseForm, setPurchaseForm] = useState({
        vendor_id: "",
        items: [{ part_id: "", quantity: 0, unit_cost: 0, total_cost: 0 }],
        invoice_no: "",
        invoice_date: new Date().toISOString().split("T")[0],
    });

    // Transfer form state
    const [transferForm, setTransferForm] = useState({
        from_location: "",
        to_location: "",
        items: [{ part_id: "", quantity: 0 }],
    });

    // Adjustment form state
    const [adjustmentForm, setAdjustmentForm] = useState({
        part_id: "",
        quantity_change: 0,
        reason: "",
    });

    useEffect(() => {
        fetchVendors();
        fetchParts();
    }, []);

    const fetchVendors = async () => {
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const res = await axios.get(`${rootUrl}/api/vendors/active`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setVendors(res.data?.data || []);
        } catch (err) {
            console.error("Error fetching vendors:", err);
            setMessage({ type: "error", text: "Failed to load vendors" });
        }
    };

    const fetchParts = async () => {
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const res = await axios.get(`${rootUrl}/car_parts`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setParts(res.data?.data || []);
        } catch (err) {
            console.error("Error fetching parts:", err);
            setMessage({ type: "error", text: "Failed to load parts" });
        }
    };

    // Purchase Handlers
    const handlePurchaseChange = (field, value) => {
        setPurchaseForm((f) => ({ ...f, [field]: value }));
    };

    const handlePurchaseItemChange = (index, field, value) => {
        const newItems = [...purchaseForm.items];
        newItems[index] = { ...newItems[index], [field]: value };

        // Auto-calculate total cost
        if (field === "quantity" || field === "unit_cost") {
            newItems[index].total_cost = newItems[index].quantity * newItems[index].unit_cost;
        }

        setPurchaseForm((f) => ({ ...f, items: newItems }));
    };

    const addPurchaseItem = () => {
        setPurchaseForm((f) => ({
            ...f,
            items: [...f.items, { part_id: "", quantity: 0, unit_cost: 0, total_cost: 0 }],
        }));
    };

    const removePurchaseItem = (index) => {
        setPurchaseForm((f) => ({
            ...f,
            items: f.items.filter((_, i) => i !== index),
        }));
    };

    const submitPurchase = async (e) => {
        e.preventDefault();
        if (!purchaseForm.vendor_id) {
            setMessage({ type: "error", text: "Please select a vendor" });
            return;
        }
        if (purchaseForm.items.length === 0 || !purchaseForm.items[0].part_id) {
            setMessage({ type: "error", text: "Please add at least one item" });
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const payload = {
                vendor_id: purchaseForm.vendor_id,
                items: purchaseForm.items.filter((item) => item.part_id),
                invoice_no: purchaseForm.invoice_no,
                invoice_date: purchaseForm.invoice_date,
                input_by: user?.username || "system",
            };

            const res = await axios.post(`${rootUrl}/transactions/posting/purchase`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (res.data?.success) {
                setMessage({ type: "success", text: "Purchase transaction posted successfully" });
                setPurchaseForm({
                    vendor_id: "",
                    items: [{ part_id: "", quantity: 0, unit_cost: 0, total_cost: 0 }],
                    invoice_no: "",
                    invoice_date: new Date().toISOString().split("T")[0],
                });
                clearMessage();
            }
        } catch (err) {
            setMessage({ type: "error", text: err.response?.data?.message || "Failed to post purchase" });
        } finally {
            setLoading(false);
        }
    };

    // Transfer Handlers
    const handleTransferChange = (field, value) => {
        setTransferForm((f) => ({ ...f, [field]: value }));
    };

    const handleTransferItemChange = (index, field, value) => {
        const newItems = [...transferForm.items];
        newItems[index] = { ...newItems[index], [field]: value };
        setTransferForm((f) => ({ ...f, items: newItems }));
    };

    const addTransferItem = () => {
        setTransferForm((f) => ({
            ...f,
            items: [...f.items, { part_id: "", quantity: 0 }],
        }));
    };

    const removeTransferItem = (index) => {
        setTransferForm((f) => ({
            ...f,
            items: f.items.filter((_, i) => i !== index),
        }));
    };

    const submitTransfer = async (e) => {
        e.preventDefault();
        if (!transferForm.from_location || !transferForm.to_location) {
            setMessage({ type: "error", text: "Please select from and to locations" });
            return;
        }
        if (transferForm.items.length === 0 || !transferForm.items[0].part_id) {
            setMessage({ type: "error", text: "Please add at least one item" });
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const payload = {
                from_location: transferForm.from_location,
                to_location: transferForm.to_location,
                items: transferForm.items.filter((item) => item.part_id),
                input_by: user?.username || "system",
            };

            const res = await axios.post(`${rootUrl}/transactions/posting/transfer`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (res.data?.success) {
                setMessage({ type: "success", text: "Inventory transfer posted successfully" });
                setTransferForm({
                    from_location: "",
                    to_location: "",
                    items: [{ part_id: "", quantity: 0 }],
                });
                clearMessage();
            }
        } catch (err) {
            setMessage({ type: "error", text: err.response?.data?.message || "Failed to post transfer" });
        } finally {
            setLoading(false);
        }
    };

    // Adjustment Handlers
    const handleAdjustmentChange = (field, value) => {
        setAdjustmentForm((f) => ({ ...f, [field]: value }));
    };

    const submitAdjustment = async (e) => {
        e.preventDefault();
        if (!adjustmentForm.part_id) {
            setMessage({ type: "error", text: "Please select a part" });
            return;
        }
        if (adjustmentForm.quantity_change === 0 || adjustmentForm.quantity_change === "") {
            setMessage({ type: "error", text: "Quantity change cannot be zero or empty" });
            return;
        }
        if (!adjustmentForm.reason || adjustmentForm.reason.trim() === "") {
            setMessage({ type: "error", text: "Please provide a reason for this adjustment" });
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const payload = {
                part_id: adjustmentForm.part_id,
                quantity_change: parseFloat(adjustmentForm.quantity_change),
                reason: adjustmentForm.reason,
                input_by: user?.username || "system",
            };

            const res = await axios.post(`${rootUrl}/transactions/posting/adjust`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (res.data?.success) {
                setMessage({ type: "success", text: "Inventory adjustment posted successfully" });
                setAdjustmentForm({ part_id: "", quantity_change: 0, reason: "" });
                clearMessage();
            }
        } catch (err) {
            setMessage({ type: "error", text: err.response?.data?.message || "Failed to post adjustment" });
        } finally {
            setLoading(false);
        }
    };

    // Helper functions
    const getPurchaseTotal = () => {
        return purchaseForm.items.reduce((sum, item) => sum + (item.total_cost || 0), 0);
    };

    // eslint-disable-next-line no-unused-vars
    const getPartName = (partId) => {
        const part = parts.find((p) => p.id === parseInt(partId));
        return part ? `${part.code || ''} - ${part.description || part.name}` : 'Select Part';
    };

    const getPartStock = (partId) => {
        const part = parts.find((p) => p.id === parseInt(partId));
        return part ? part.stock_quantity || 0 : 0;
    };

    // eslint-disable-next-line no-unused-vars
    const getVendorName = (vendorId) => {
        const vendor = vendors.find((v) => v.id === parseInt(vendorId));
        return vendor ? vendor.supplier_name || vendor.name : 'Select Vendor';
    };

    const clearMessage = () => setTimeout(() => setMessage({ type: "", text: "" }), 3000);

    return (
        <Page>
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-[#2a2e30] mb-4">Post Transaction</h1>

                {message.text && (
                    <div
                        className={`p-4 rounded-lg mb-4 ${message.type === "success"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                            }`}
                    >
                        {message.text}
                    </div>
                )}

                <div className="flex gap-2 mb-6 border-b">
                    <button
                        onClick={() => setActiveTab("purchase")}
                        className={`px-6 py-3 font-semibold ${activeTab === "purchase"
                            ? "border-b-2 border-[#FF5733] text-[#FF5733]"
                            : "text-gray-600 hover:text-gray-900"
                            }`}
                    >
                        Purchases
                    </button>
                    <button
                        onClick={() => setActiveTab("transfer")}
                        className={`px-6 py-3 font-semibold ${activeTab === "transfer"
                            ? "border-b-2 border-[#FF5733] text-[#FF5733]"
                            : "text-gray-600 hover:text-gray-900"
                            }`}
                    >
                        Transfers
                    </button>
                    <button
                        onClick={() => setActiveTab("adjustment")}
                        className={`px-6 py-3 font-semibold ${activeTab === "adjustment"
                            ? "border-b-2 border-[#FF5733] text-[#FF5733]"
                            : "text-gray-600 hover:text-gray-900"
                            }`}
                    >
                        Adjustments
                    </button>
                </div>
            </div>

            {/* Purchase Tab */}
            {activeTab === "purchase" && (
                <form onSubmit={submitPurchase} className="space-y-6">
                    {!vendors.length && (
                        <div className="p-4 bg-yellow-100 text-yellow-800 rounded-lg">
                            No active vendors available. Please add vendors first.
                        </div>
                    )}
                    {!parts.length && (
                        <div className="p-4 bg-yellow-100 text-yellow-800 rounded-lg">
                            No parts available. Please add parts first.
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Vendor *</label>
                            <select
                                value={purchaseForm.vendor_id}
                                onChange={(e) => handlePurchaseChange("vendor_id", e.target.value)}
                                className="w-full border rounded-lg px-3 py-2"
                                required
                            >
                                <option value="">Select Vendor</option>
                                {vendors.map((v) => (
                                    <option key={v.id} value={v.id}>
                                        {v.supplier_name || v.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Date *</label>
                            <input
                                type="date"
                                value={purchaseForm.invoice_date}
                                onChange={(e) => handlePurchaseChange("invoice_date", e.target.value)}
                                className="w-full border rounded-lg px-3 py-2"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Number</label>
                            <input
                                type="text"
                                value={purchaseForm.invoice_no}
                                onChange={(e) => handlePurchaseChange("invoice_no", e.target.value)}
                                className="w-full border rounded-lg px-3 py-2"
                                placeholder="Optional supplier invoice number"
                            />
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Items</h3>
                        <div className="space-y-3">
                            {purchaseForm.items.map((item, idx) => (
                                <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
                                    <select
                                        value={item.part_id}
                                        onChange={(e) => handlePurchaseItemChange(idx, "part_id", e.target.value)}
                                        className="border rounded-lg px-3 py-2 text-sm"
                                        required={idx === 0}
                                    >
                                        <option value="">Select Part</option>
                                        {parts.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.code ? `${p.code} - ` : ''}{p.description || p.name} (Stock: {p.stock_quantity || 0})
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        type="number"
                                        value={item.quantity}
                                        onChange={(e) => handlePurchaseItemChange(idx, "quantity", parseFloat(e.target.value) || 0)}
                                        placeholder="Qty"
                                        className="border rounded-lg px-3 py-2"
                                        min="0"
                                        step="0.01"
                                        required={!!item.part_id}
                                    />
                                    <input
                                        type="number"
                                        value={item.unit_cost}
                                        onChange={(e) => handlePurchaseItemChange(idx, "unit_cost", parseFloat(e.target.value) || 0)}
                                        placeholder="Unit Cost"
                                        className="border rounded-lg px-3 py-2"
                                        step="0.01"
                                        min="0"
                                        required={!!item.part_id}
                                    />
                                    <input
                                        type="number"
                                        value={item.total_cost}
                                        disabled
                                        className="border rounded-lg px-3 py-2 bg-gray-100"
                                        step="0.01"
                                    />
                                    {purchaseForm.items.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removePurchaseItem(idx)}
                                            className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={addPurchaseItem}
                            className="mt-3 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                        >
                            + Add Item
                        </button>

                        {/* Purchase Summary */}
                        <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                            <h4 className="font-semibold text-gray-800 mb-3">Purchase Summary</h4>
                            <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span>Items:</span>
                                    <span className="font-medium">{purchaseForm.items.filter(i => i.part_id).length}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span>Total Units:</span>
                                    <span className="font-medium">{purchaseForm.items.reduce((sum, i) => sum + (i.quantity || 0), 0)}</span>
                                </div>
                                <div className="border-t border-gray-300 my-2"></div>
                                <div className="flex justify-between text-lg font-bold text-[#FF5733]">
                                    <span>Purchase Total:</span>
                                    <span>{getPurchaseTotal().toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 bg-[#FF5733] text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-400"
                        >
                            {loading ? "Processing..." : "Post Purchase"}
                        </button>
                    </div>
                </form>
            )}

            {/* Transfer Tab */}
            {activeTab === "transfer" && (
                <form onSubmit={submitTransfer} className="space-y-6">
                    {!parts.length && (
                        <div className="p-4 bg-yellow-100 text-yellow-800 rounded-lg">
                            No parts available. Please add parts first.
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">From Location *</label>
                            <input
                                type="text"
                                value={transferForm.from_location}
                                onChange={(e) => handleTransferChange("from_location", e.target.value)}
                                className="w-full border rounded-lg px-3 py-2"
                                placeholder="e.g., Workshop"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">To Location *</label>
                            <input
                                type="text"
                                value={transferForm.to_location}
                                onChange={(e) => handleTransferChange("to_location", e.target.value)}
                                className="w-full border rounded-lg px-3 py-2"
                                placeholder="e.g., Store"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Items</h3>
                        <div className="space-y-3">
                            {transferForm.items.map((item, idx) => (
                                <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end p-3 border rounded-lg bg-gray-50">
                                    <select
                                        value={item.part_id}
                                        onChange={(e) => handleTransferItemChange(idx, "part_id", e.target.value)}
                                        className="md:col-span-2 border rounded-lg px-3 py-2"
                                        required={true}
                                    >
                                        <option value="">Select Part</option>
                                        {parts.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.code ? `${p.code} - ` : ''}{p.description || p.name} (Stock: {p.stock_quantity || 0})
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        type="number"
                                        value={item.quantity}
                                        onChange={(e) => handleTransferItemChange(idx, "quantity", parseFloat(e.target.value) || 0)}
                                        placeholder="Quantity"
                                        min="0"
                                        step="0.01"
                                        required={!!item.part_id}
                                        className="border rounded-lg px-3 py-2"
                                    />
                                    {item.part_id && (
                                        <div className="text-sm text-gray-600">
                                            Stock: <span className="font-semibold">{getPartStock(item.part_id)}</span>
                                        </div>
                                    )}
                                    {transferForm.items.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeTransferItem(idx)}
                                            className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={addTransferItem}
                            className="mt-3 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                        >
                            + Add Item
                        </button>

                        {/* Transfer Summary */}
                        <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                            <h4 className="font-semibold text-gray-800 mb-3">Transfer Summary</h4>
                            <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span>Items:</span>
                                    <span className="font-medium">{transferForm.items.filter(i => i.part_id).length}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span>Total Units:</span>
                                    <span className="font-medium">{transferForm.items.reduce((sum, i) => sum + (i.quantity || 0), 0)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 bg-[#FF5733] text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-400"
                        >
                            {loading ? "Processing..." : "Post Transfer"}
                        </button>
                    </div>
                </form>
            )}

            {/* Adjustment Tab */}
            {activeTab === "adjustment" && (
                <form onSubmit={submitAdjustment} className="space-y-6">
                    {!parts.length && (
                        <div className="p-4 bg-yellow-100 text-yellow-800 rounded-lg">
                            No parts available. Please add parts first.
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Part *</label>
                            <select
                                value={adjustmentForm.part_id}
                                onChange={(e) => handleAdjustmentChange("part_id", e.target.value)}
                                className="w-full border rounded-lg px-3 py-2"
                                required={true}
                            >
                                <option value="">Select Part</option>
                                {parts.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.code ? `${p.code} - ` : ''}{p.description || p.name} (Stock: {p.stock_quantity || 0})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Quantity Change *</label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    value={adjustmentForm.quantity_change}
                                    onChange={(e) => handleAdjustmentChange("quantity_change", e.target.value)}
                                    className="flex-1 border rounded-lg px-3 py-2"
                                    placeholder="Use + or - value"
                                    step="0.01"
                                    required={!!adjustmentForm.part_id}
                                />
                                <div className="text-sm text-gray-600 whitespace-nowrap">
                                    {adjustmentForm.quantity_change > 0 ? (
                                        <span className="text-green-600 font-semibold">+ Add</span>
                                    ) : adjustmentForm.quantity_change < 0 ? (
                                        <span className="text-red-600 font-semibold">- Remove</span>
                                    ) : (
                                        <span className="text-gray-500">No change</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {adjustmentForm.part_id && (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-sm">
                                <div className="flex justify-between mb-2">
                                    <span className="text-gray-700">Current Stock:</span>
                                    <span className="font-semibold">{getPartStock(adjustmentForm.part_id)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-700">After Adjustment:</span>
                                    <span className="font-semibold text-[#FF5733]">
                                        {(getPartStock(adjustmentForm.part_id) + (parseFloat(adjustmentForm.quantity_change) || 0)).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Adjustment *</label>
                        <textarea
                            value={adjustmentForm.reason}
                            onChange={(e) => handleAdjustmentChange("reason", e.target.value)}
                            className="w-full border rounded-lg px-3 py-2"
                            rows={4}
                            placeholder="Explain the reason for this adjustment (required for audit trail)"
                            required={true}
                        />
                        <p className="mt-1 text-xs text-gray-500">This reason will be recorded for audit purposes</p>
                    </div>

                    <div className="flex gap-3">
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 bg-[#FF5733] text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-400"
                        >
                            {loading ? "Processing..." : "Post Adjustment"}
                        </button>
                    </div>
                </form>
            )}
        </Page>
    );
}
